import { ChangeEvent, FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Pagination, Typography } from '@mui/material';
import cn from 'classnames';

import Loadable from '@components/Loadable/Loadable';
import { InquiryDetails } from '@models/inquiries';
import { handleGetInquiryList } from '@store/features/inquiries/list/actions';
import {
  inquiriesListErrorSelector,
  inquiriesListIsLoadingSelector,
  inquiriesListPaginationSelector,
  inquiriesListSelector,
} from '@store/features/inquiries/list/selectors';
import { TInquiriesListKey } from '@store/features/inquiries/list/slice.interface';
import { setInquiryToShare } from '@store/features/inquiries/share/slice';
import useDispatch from '@store/hooks/useDispatch';
import formatDate from '@utils/formatDate';

import FoldableInquiryCard from './FoldableInquiryCard/FoldableInquiryCard';
import InquiryListPlaceholder from './InquiryListPlaceholder/InquiryListPlaceholder';
import { IInquiryListProps } from './InquiryList.interface';

import './style.scss';

const InquiryList: FC<IInquiryListProps> = ({ className, title, isComplete = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const key = isComplete ? TInquiriesListKey.Complete : TInquiriesListKey.InProgress;
  const data = useSelector(inquiriesListSelector(key));
  const isLoading = useSelector(inquiriesListIsLoadingSelector(key));
  const error = useSelector(inquiriesListErrorSelector(key));
  const pagination = useSelector(inquiriesListPaginationSelector(key));

  const inquiriesList: InquiryDetails[] = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    return data;
  }, [data]);

  const handleFetchInquiries = useCallback(() => {
    dispatch(handleGetInquiryList({ key, isComplete, page: 1 }));
  }, [dispatch, isComplete, key]);

  const handlePagination = useCallback(
    (event: ChangeEvent<unknown>, page: number) => {
      dispatch(handleGetInquiryList({ key, isComplete, page }));
    },
    [dispatch, isComplete, key],
  );

  const handleShareResponse = useCallback(
    (inquiry: InquiryDetails) => {
      dispatch(setInquiryToShare(inquiry));
    },
    [dispatch],
  );

  useEffect(handleFetchInquiries, [handleFetchInquiries]);

  return (
    <div className={cn('inquiry-cards', className)}>
      <Typography variant="h2" className="inquiry-list__title">
        {title}
      </Typography>

      <div className="inquiry-list__content">
        <Loadable
          isLoading={isLoading}
          error={error && t('errors.inquiryListError')}
          onRetry={handleFetchInquiries}
        >
          {inquiriesList?.length ? (
            inquiriesList.map(inquiry => (
              <FoldableInquiryCard
                className="inquiry-list__card"
                inquiryId={inquiry.id}
                inquiryName={inquiry.name}
                isSupplementary={inquiry.isSupplementary}
                type={
                  inquiry.isSupplementary ? t('inquiries.supplement') : t('inquiries.medication')
                }
                date={formatDate(inquiry.createdOn)}
                statusId={inquiry.inquiryStatusId}
                items={inquiry.inquiryItems}
                onShare={isComplete ? () => handleShareResponse(inquiry) : undefined}
                key={inquiry.id}
              />
            ))
          ) : (
            <InquiryListPlaceholder isComplete={isComplete} />
          )}
        </Loadable>
      </div>

      {!!pagination.pageCount && pagination.pageCount > 1 && (
        <Pagination
          className="inquiry-list__pagination"
          color="primary"
          page={pagination.page}
          count={pagination.pageCount}
          onChange={handlePagination}
        />
      )}
    </div>
  );
};

export default InquiryList;
